/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { getUnreadCount } from '../lib/api'

interface NotificationCountContextType {
  unreadCount: number
  setUnreadCount: (count: number | ((prev: number) => number)) => void
  /** Force an immediate refresh of the unread count from the server */
  refreshCount: () => Promise<void>
  /** Optimistically adjust the count without waiting for server */
  adjustCount: (delta: number) => void
}

const NotificationCountContext = createContext<NotificationCountContextType>({
  unreadCount: 0,
  setUnreadCount: () => {},
  refreshCount: async () => {},
  adjustCount: () => {},
})

export function NotificationCountProvider({ children }: { children: ReactNode }) {
  const [unreadCount, setUnreadCount] = useState(0)

  const refreshCount = useCallback(async () => {
    try {
      const { count } = await getUnreadCount()
      setUnreadCount(count)
    } catch {
      // silently fail — next poll cycle will catch up
    }
  }, [])

  const adjustCount = useCallback((delta: number) => {
    setUnreadCount(prev => Math.max(0, prev + delta))
  }, [])

  return (
    <NotificationCountContext.Provider value={{ unreadCount, setUnreadCount, refreshCount, adjustCount }}>
      {children}
    </NotificationCountContext.Provider>
  )
}

export function useNotificationCount() {
  return useContext(NotificationCountContext)
}
